/**
 * Sending a material order to the supplier.
 *
 * There is no supplier API (ADR 0093), so placing an order is a person putting
 * the list in front of another person. What that list says lives here rather
 * than in the modal, so the wording is tested, so the MCP server can compose the
 * same letter, and so the one rule with money attached to it — the QuickMeasure
 * credit — cannot be got wrong by a screen that forgets to ask.
 *
 * **The credit.** Jonathon, 2026-09-06: "heely has offered to credit our account
 * when we order a quick measure report and then place an order for the
 * materials." Heely-Brown pays for the report if the material comes to them, but
 * only if somebody asks; a rep who forgets has quietly bought a report that
 * could have been free (ADR 0110). So the ask writes itself into the order
 * email, once per job, whenever the two facts line up: this job ordered a
 * QuickMeasure report, and this supplier is one that credits it.
 */

import { jobAddress } from "./search.js";
import type {
  Job,
  MaterialOrder,
  MaterialOrderItem,
  Supplier,
} from "./types.js";

/** A job's material orders, as the credit rule needs to see them. */
export interface CreditHistory {
  /** Every material order on this job, this one included. */
  orders: readonly Pick<
    MaterialOrder,
    "id" | "quickmeasure_credit_requested_at"
  >[];
}

export interface QuickMeasureCredit {
  /** Ask for it in this email. */
  claim: boolean;
  /** The paragraph that does the asking, or "" when there is nothing to ask. */
  note: string;
  /**
   * Why it is not being asked for, for the rep — null when it is.
   *
   * Said out loud on the screen rather than left as an absence: "no credit
   * line" and "no credit line because nobody recorded the report" look
   * identical, and only one of them is worth fixing before pressing send.
   */
  why: string | null;
}

/** The date, as a person writes it to a supplier. */
function onDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

/**
 * Whether this order's email should ask for the QuickMeasure credit.
 *
 * Four things have to be true, and each failure has its own sentence because
 * each has its own fix: the supplier has to be one that credits (a checkbox in
 * Tools), the job has to have ordered a report (the Draw Roof step records it),
 * the report has to not have been claimed already on another order for the same
 * job, and there has to be an address to name it by.
 */
export function quickMeasureCredit(input: {
  job: Job;
  supplier: Pick<Supplier, "name" | "credits_quickmeasure"> | null;
  order: Pick<MaterialOrder, "id" | "quickmeasure_credit_requested_at">;
  history: CreditHistory;
}): QuickMeasureCredit {
  const { job, supplier, order, history } = input;
  const nothing = (why: string | null): QuickMeasureCredit => ({
    claim: false,
    note: "",
    why,
  });

  if (!supplier) return nothing(null);
  if (!supplier.credits_quickmeasure) {
    return nothing(null);
  }
  if (!job.quickmeasure_ordered_at) {
    return nothing(
      `${supplier.name} credits a QuickMeasure report, and no report was ordered for this job from Roof Board. If one was ordered another way, ask ${supplier.name} for the credit yourself.`,
    );
  }

  const claimedElsewhere = history.orders.find(
    (o) => o.id !== order.id && o.quickmeasure_credit_requested_at,
  );
  if (claimedElsewhere) {
    return nothing(
      "The QuickMeasure credit for this job has already been asked for on another material order. One report, one credit.",
    );
  }
  if (order.quickmeasure_credit_requested_at) {
    return nothing(
      `Asked for on ${onDate(order.quickmeasure_credit_requested_at)}. It is not asked for twice.`,
    );
  }

  const where = jobAddress(job);
  return {
    claim: true,
    note: [
      `We ordered a GAF QuickMeasure report for ${where || "this address"} on ${onDate(
        job.quickmeasure_ordered_at,
      )}.`,
      `Please credit our account for the cost of it against this order, as agreed.`,
    ].join(" "),
    why: null,
  };
}

/**
 * One line of the order, as the supplier reads it.
 *
 * The line's own name — a material line has `name` and nothing else that says
 * what it is, and an order line reading "12 SQ" and no more is an order nobody
 * can fill. (This used to read `description` first and fall back to `name`.
 * `material_order_items` has no `description`, so the fallback was the whole
 * behaviour.)
 */
function orderLine(
  item: Pick<MaterialOrderItem, "qty" | "unit" | "name" | "color">,
): string {
  const what = (item.name ?? "").trim();
  return `  ${item.qty ?? 0} ${item.unit ?? ""}  ${what}${
    item.color ? ` (${item.color})` : ""
  }`;
}

export interface SupplierOrderEmail {
  subject: string;
  body: string;
}

/**
 * The order email: what it says, and what it asks for.
 *
 * The order itself goes as a PDF, because that is what a counter prints and
 * walks to the racks with. The letter is the covering note — who it is for and
 * where it goes — and it still lists the lines underneath, so a supplier who
 * reads only the email is reading the same order as one who opens the file, and
 * so "Copy order" still has something to copy for a phone call.
 */
export function supplierOrderEmail(input: {
  order: Pick<
    MaterialOrder,
    "order_number" | "pickup" | "delivery_date" | "delivery_note"
  >;
  items: readonly Pick<MaterialOrderItem, "qty" | "unit" | "name" | "color">[];
  /** Unused in the wording — kept so a caller cannot forget which supplier. */
  supplierName: string | null;
  job: Job;
  /** From `quickMeasureCredit`. Its note goes above the lines, where it is read. */
  credit?: QuickMeasureCredit | undefined;
  /** The measurement report hanging off it, named so the body points at it. */
  attachmentName?: string | null;
  /** The order's own PDF, likewise — the thing the counter actually prints. */
  orderPdfName?: string | null;
}): SupplierOrderEmail {
  const { order, items, job, credit, attachmentName, orderPdfName } = input;
  const where = jobAddress(job);
  const number = `MO-${order.order_number}`;

  const lines = items.map(orderLine).join("\n");

  const delivery = order.pickup
    ? "We will pick this up at the counter."
    : `Please deliver to ${where || "the job address"} on ${
        order.delivery_date ?? "a date to be confirmed"
      }.`;

  const attached = [
    orderPdfName ? `the order as a PDF (${orderPdfName})` : "",
    attachmentName ? `the roof's measurement report (${attachmentName})` : "",
  ].filter(Boolean);

  const body = [
    `Please put in the following order for job ${job.job_number ?? ""}`.trim() +
      (where ? `, ${where}.` : "."),
    "",
    `Order number: ${number}`,
    delivery,
    order.delivery_note ? `Note: ${order.delivery_note}` : "",
    attached.length ? `Attached: ${attached.join(" and ")}.` : "",
    "",
    lines || "  (no line items)",
    "",
    credit?.claim ? credit.note : "",
    "Thank you.",
  ]
    .filter((line, at, all) => !(line === "" && all[at - 1] === ""))
    .join("\n")
    .trim();

  return {
    subject: `Material order ${number}${where ? ` — ${where}` : ""}`,
    body: `${body}\n`,
  };
}
